import { Button, DialogActions, DialogContent } from "@material-ui/core";
import { ServiceRoleForm } from "../../Forms/Settings";
import { useSnackbar } from "notistack";
import React, { useRef } from "react";
import { useStoreon } from "storeon/react";
import { DialogContextProps } from "@interfaces/System/DialogContextProps";
import { TableEvents, TableState } from "@store/table-module";
import { serviceRoleService } from "@services/Settings";
import { ModalContentLoading } from "../../Common/Modal";
import { ServiceRole } from "@interfaces/ServiceRole";
import { CreateServiceRoleDto, UpdateServiceRoleDto } from "@dto/SettingsDto/ServiceRolesDto";
import { useAsync, useLoading } from "@hooks";

const ServiceRoleDialogContext = ({ onClose, modalProps }: DialogContextProps) => {
    const { itemId } = { ...modalProps };

    const formRef = useRef<any>();
    const { enqueueSnackbar } = useSnackbar();
    const { dispatch: tableDispatch } = useStoreon<TableState, TableEvents>();
    const { loadingStatus: submitLoadingStatus, setLoadingStatus: setSubmitLoadingStatus } =
        useLoading("inactivity");

    const { data, loadingStatus } = useAsync<ServiceRole>(serviceRoleService.getOneServiceRole, {
        functionArgs: [itemId],
        canLoad: !!itemId,
    });

    const invokeSubmit = () => {
        formRef.current.handleSubmit();
    };

    const handleCreateServiceRole = async (values) => {
        setSubmitLoadingStatus("loading");
        try {
            const dto = serviceRoleService.plainToClass(CreateServiceRoleDto, values);

            await serviceRoleService.createServiceRole(dto);
            enqueueSnackbar("Роль успешно создана", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при создании роли", {
                variant: "error",
            });
        }
        setSubmitLoadingStatus("loaded");
    };

    const handleUpdateServiceRole = async (values) => {
        if (!itemId) return;

        setSubmitLoadingStatus("loading");
        try {
            const dto = serviceRoleService.plainToClass(UpdateServiceRoleDto, values);

            await serviceRoleService.updateServiceRole(itemId, dto);
            enqueueSnackbar("Роль успешно обновлена", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при обновлении роли", {
                variant: "error",
            });
        }
        setSubmitLoadingStatus("loaded");
    };

    return (
        <>
            <DialogContent>
                {loadingStatus === "loading" && <ModalContentLoading />}

                {(loadingStatus === "loaded" || loadingStatus === "inactivity") && (
                    <ServiceRoleForm
                        ref={formRef}
                        onSubmit={itemId ? handleUpdateServiceRole : handleCreateServiceRole}
                        data={itemId ? data : undefined}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="default" onClick={onClose}>
                    Отмена
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={invokeSubmit}
                    disabled={submitLoadingStatus === "loading"}
                >
                    Сохранить
                </Button>
            </DialogActions>
        </>
    );
};

export default ServiceRoleDialogContext;
